import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";

const STRENGTHS = [
  { icon: "ri-shield-star-line", title: "MOOG 제품 기반 기술 대응", desc: "서보밸브, 모션컨트롤러 등 MOOG 제품에 대한 깊은 이해와 대응" },
  { icon: "ri-water-flash-line", title: "Servo Hydraulic System 경험", desc: "다양한 산업 현장의 서보유압 제어 시스템 구축 경험" },
  { icon: "ri-stack-line", title: "Roll Press 제어 시스템 대응", desc: "롤프레스 갭/압력/위치 제어를 위한 통합 시스템 구성" },
  { icon: "ri-cpu-line", title: "Motion Controller & I/O", desc: "MSC III, PLC, EtherCAT, Analog/Digital I/O 구성 지원" },
  { icon: "ri-radar-line", title: "센서 피드백 기반 제어", desc: "위치/압력 센서 피드백 기반 폐루프 제어 설계" },
  { icon: "ri-tools-line", title: "현장 시운전 및 문제 해결", desc: "납품에서 끝나지 않고 현장 안정화까지 책임" },
];

export default function CompanyPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Company"
        title="About MC TECH"
        subtitle="산업용 모션제어 및 서보유압 시스템 기반 정밀 제어 솔루션 전문 엔지니어링 회사"
        crumbs={[{ label: "HOME", to: "/" }, { label: "Company" }]}
        imageUrl="https://readdy.ai/api/search-image?query=Modern%20industrial%20engineering%20office%20with%20engineers%20discussing%20hydraulic%20schematics%20on%20large%20screen%2C%20clean%20professional%20environment%2C%20cool%20industrial%20lighting%2C%20editorial%20B2B%20engineering%20photography%2C%20muted%20steel%20gray%20and%20cool%20blue%20palette&width=1920&height=900&seq=mctech-company-hero-01&orientation=landscape"
      />

      {/* Intro */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-4">
              <span className="w-6 h-px bg-accent-500" />
              Who We Are
            </span>
            <h2 className="text-3xl md:text-[42px] font-heading font-extrabold text-foreground-950 leading-[1.25]">
              제품 공급을 넘어,
              <br />
              <span className="text-primary-700">장비의 제어 시스템</span>
              <span className="whitespace-nowrap">을 완성합니다.</span>
            </h2>
            <div className="mt-6 md:mt-8 space-y-5 md:space-y-6 text-sm md:text-base text-foreground-700 leading-[1.8]">
              <p>
                MC TECH는 산업용 모션제어와 서보유압 시스템을 기반으로 다양한 장비에 필요한 정밀 제어 솔루션을 제공합니다.
              </p>
              <p>
                서보밸브, 유압 유니트, 모션 컨트롤러, 서보드라이브, 서보모터, 위치·압력 센서 등을 활용하여 장비 사양에 맞는 제어 시스템을 구성합니다.
              </p>
              <p>
                제품 공급부터 기술 검토, 회로 구성, 파라미터 설정, 현장 시운전과 유지보수까지 지원하며 롤프레스를 비롯한 산업 장비의 안정적인 운전을 돕습니다.
              </p>
            </div>

            <div className="mt-10 md:mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: "ri-hand-coin-line", title: "MOOG 제품 공급" },
                { icon: "ri-settings-3-line", title: "유압·전기 통합 설계" },
                { icon: "ri-equalizer-line", title: "파라미터 설정" },
                { icon: "ri-customer-service-2-line", title: "현장 시운전 지원" },
              ].map((s) => (
                <div key={s.title} className="bg-background-100 border border-background-200 rounded-xl p-5 md:p-6 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-3">
                    <i className={`${s.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                  </div>
                  <div className="text-sm md:text-base font-heading font-bold text-foreground-950">
                    {s.title}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-background-200 h-80 lg:h-[500px]">
              <img
                src="https://readdy.ai/api/search-image?query=Industrial%20technician%20hands%20adjusting%20servo%20drive%20and%20hydraulic%20control%20cabinet%20wiring%20with%20sensors%20and%20pressure%20gauges%20in%20clean%20modern%20factory%2C%20equipment%20and%20cables%20in%20sharp%20focus%2C%20no%20prominent%20human%20face%20visible%2C%20cool%20industrial%20lighting%2C%20detailed%20mechanical%20textures%2C%20editorial%20B2B%20engineering%20photography%2C%20muted%20steel%20gray%20and%20cool%20blue%20palette&width=1000&height=1400&seq=mctech-company-equipment-03&orientation=portrait"
                alt="MC TECH Control System Equipment"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-background-50/95 backdrop-blur rounded-lg p-4 md:p-5">
                <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-secondary-600">
                  Mission
                </div>
                <div className="mt-1 text-sm md:text-base font-heading font-bold text-foreground-950 leading-snug">
                  정밀한 압력·위치·갭 제어 시스템 구현
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strengths */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-12 md:mb-14 text-center">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-4">
              <span className="w-6 h-px bg-accent-500" />
              Our Strengths
              <span className="w-6 h-px bg-accent-500" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950">
              회사 강점
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {STRENGTHS.map((s, i) => (
              <div
                key={s.title}
                className="bg-background-50 border border-background-200 rounded-xl p-6 md:p-7 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                    <i className={`${s.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                  </div>
                  <span className="text-xs font-heading font-bold text-secondary-500">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm md:text-base text-foreground-700 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="MC TECH와 함께 정밀 제어 시스템을 완성해보세요."
        description="상담은 언제든 환영합니다. 도면이나 사양 정보가 있으시면 함께 보내주세요."
      />
    </SiteLayout>
  );
}